/**
 * Connexion, création de compte et mot de passe oublié (design system, EcranConnexion),
 * dans une colonne étroite centrée, sans barre latérale.
 */
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Bouton } from '../composants/Bouton';
import { ChampTexte } from '../composants/ChampTexte';
import { Icone } from '../composants/Icone';
import { MessageDeChamp } from '../composants/MessageDeChamp';
import {
  ErreurCompte,
  LONGUEUR_MOT_DE_PASSE,
  type Authentification,
} from '../donnees/authentification';

export type VueConnexion =
  'connexion' | 'creation' | 'oubli' | 'lien-envoye' | 'nouveau-mot-de-passe';

const TITRES: Record<VueConnexion, string> = {
  connexion: 'Se connecter',
  creation: 'Créer un compte',
  oubli: 'Mot de passe oublié',
  'lien-envoye': 'Vérifiez votre messagerie',
  'nouveau-mot-de-passe': 'Choisir un nouveau mot de passe',
};

function Lien({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <a
      className="fc-ecran-connexion__lien"
      href="#"
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
    >
      {children}
    </a>
  );
}

export function EcranConnexion({
  auth,
  vueInitiale = 'connexion',
  onMotDePasseChange,
}: {
  auth: Authentification;
  vueInitiale?: VueConnexion;
  onMotDePasseChange?: () => void;
}) {
  const [vue, setVue] = useState<VueConnexion>(vueInitiale);
  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [erreurMotDePasse, setErreurMotDePasse] = useState<string | null>(null);

  useEffect(() => setVue(vueInitiale), [vueInitiale]);
  useEffect(() => {
    document.title = `${TITRES[vue]} — Flashcard`;
  }, [vue]);

  const changerDeVue = (nouvelle: VueConnexion) => {
    setVue(nouvelle);
    setErreur(null);
    setErreurMotDePasse(null);
    setMotDePasse('');
  };

  async function envoyer(e: FormEvent) {
    e.preventDefault();
    setErreur(null);
    setErreurMotDePasse(null);
    if (
      (vue === 'creation' || vue === 'nouveau-mot-de-passe') &&
      motDePasse.length < LONGUEUR_MOT_DE_PASSE
    ) {
      setErreurMotDePasse(`Saisissez au moins ${LONGUEUR_MOT_DE_PASSE} caractères.`);
      return;
    }
    setEnvoi(true);
    try {
      if (vue === 'connexion') await auth.connexion(email.trim(), motDePasse);
      else if (vue === 'creation') await auth.creation(email.trim(), motDePasse);
      else if (vue === 'oubli') {
        await auth.demanderLien(email.trim());
        setVue('lien-envoye');
      } else if (vue === 'nouveau-mot-de-passe') {
        await auth.changerMotDePasse(motDePasse);
        onMotDePasseChange?.();
      }
    } catch (err) {
      setErreur(
        err instanceof ErreurCompte
          ? err.message
          : 'Une erreur est survenue. Réessayez dans un instant.',
      );
    } finally {
      setEnvoi(false);
    }
  }

  const champEmail = (
    <ChampTexte
      libelle="Adresse e-mail"
      type="email"
      valeur={email}
      onChange={setEmail}
      saisieAuto="email"
      exemple="vous@exemple.fr"
      lectureSeule={envoi}
      invalide={vue === 'connexion' && erreur !== null}
      autoFocus={email === ''}
      requis
    />
  );

  let contenu: ReactNode;
  if (vue === 'lien-envoye') {
    contenu = (
      <>
        <Bouton large onClick={() => changerDeVue('connexion')}>
          Revenir à la connexion
        </Bouton>
        <p className="fc-ecran-connexion__bas">
          Rien reçu&#8239;?{' '}
          <Lien onClick={() => void auth.demanderLien(email.trim())}>Renvoyer le lien</Lien>
        </p>
      </>
    );
  } else {
    const libelleBouton = {
      connexion: ['Se connecter', 'Connexion…'],
      creation: ['Créer le compte', 'Création…'],
      oubli: ['Recevoir un lien de réinitialisation', 'Envoi…'],
      'nouveau-mot-de-passe': ['Enregistrer le mot de passe', 'Enregistrement…'],
    }[vue];
    contenu = (
      <>
        <form
          className="fc-ecran-connexion__formulaire"
          noValidate
          onSubmit={envoyer}
          aria-busy={envoi || undefined}
          aria-describedby={erreur ? 'connexion-erreur' : undefined}
        >
          {vue !== 'nouveau-mot-de-passe' && champEmail}
          {vue !== 'oubli' && (
            <ChampTexte
              libelle={vue === 'nouveau-mot-de-passe' ? 'Nouveau mot de passe' : 'Mot de passe'}
              type="mot-de-passe"
              valeur={motDePasse}
              onChange={setMotDePasse}
              saisieAuto={vue === 'connexion' ? 'current-password' : 'new-password'}
              lectureSeule={envoi}
              invalide={vue === 'connexion' && erreur !== null}
              autoFocus={vue === 'nouveau-mot-de-passe'}
              {...(erreurMotDePasse ? { erreur: erreurMotDePasse } : {})}
              {...(vue === 'connexion'
                ? {
                    lien: {
                      libelle: 'Mot de passe oublié ?',
                      onClick: () => changerDeVue('oubli'),
                    },
                  }
                : {
                    aide: `Au moins ${LONGUEUR_MOT_DE_PASSE} caractères ; une phrase de plusieurs mots est plus sûre.`,
                  })}
              requis
            />
          )}
          {erreur && (
            <MessageDeChamp id="connexion-erreur" ton="erreur" role="alert">
              {erreur}
            </MessageDeChamp>
          )}
          <Bouton type="submit" variante="principal" large occupe={envoi}>
            {envoi ? libelleBouton[1] : libelleBouton[0]}
          </Bouton>
        </form>
        {vue === 'connexion' && (
          <p className="fc-ecran-connexion__bas">
            Pas encore de compte&#8239;?{' '}
            <Lien onClick={() => changerDeVue('creation')}>Créer un compte</Lien>
          </p>
        )}
        {vue === 'creation' && (
          <p className="fc-ecran-connexion__bas">
            Déjà un compte&#8239;?{' '}
            <Lien onClick={() => changerDeVue('connexion')}>Se connecter</Lien>
          </p>
        )}
        {vue === 'oubli' && (
          <p className="fc-ecran-connexion__bas">
            <Lien onClick={() => changerDeVue('connexion')}>Revenir à la connexion</Lien>
          </p>
        )}
      </>
    );
  }

  const texte = {
    connexion: null,
    creation: 'Le compte est utilisable tout de suite, sans e-mail de confirmation.',
    oubli:
      'Indiquez l’adresse de votre compte : vous recevrez un lien pour choisir un nouveau mot de passe.',
    'lien-envoye': `Un lien de réinitialisation a été envoyé à ${email || 'votre adresse'}, si un compte y est associé.`,
    'nouveau-mot-de-passe': 'Il remplacera l’ancien sur tous vos appareils.',
  }[vue];

  return (
    <div className="fc-fenetre fc-fenetre--seule">
      <header className="fc-fenetre__outils" aria-label="Barre de la fenêtre" />
      <main className="fc-fenetre__principale">
        <div className="fc-ecran-connexion">
          <div className="fc-ecran-connexion__panneau">
            <div className="fc-ecran-connexion__tete">
              <p className="fc-ecran-connexion__marque">Flashcard</p>
              {vue === 'lien-envoye' && (
                <span className="fc-ecran-connexion__icone">
                  <Icone nom="mail" />
                </span>
              )}
              <h1 className="fc-ecran-connexion__titre">{TITRES[vue]}</h1>
              {texte && (
                <p
                  className="fc-ecran-connexion__texte"
                  role={vue === 'lien-envoye' ? 'status' : undefined}
                >
                  {texte}
                </p>
              )}
            </div>
            {contenu}
          </div>
        </div>
      </main>
    </div>
  );
}
